/**
 * CitrusDoctor - Main Plant Diagnosis Component
 * 
 * Why this matters: Orchestrates the full plant diagnosis flow from image upload
 * through diagnosis to email capture and results display. Uses Gemini AI with
 * Three Plant Pillars knowledge for expert-quality recommendations.
 * 
 * Flow:
 * 1. Image Upload → 2. Analysis → 3. Preview → 4. Email Gate → 5. Full Report
 */

import React, { useState, useCallback, useEffect } from 'react';
import DOMPurify from 'dompurify';
import ImageUpload, { UploadedImage } from './ImageUpload';
import DiagnosisPreview from './DiagnosisPreview';
import EmailGate from './EmailGate';
import FullDiagnosisReport from './FullDiagnosisReport';
import './CitrusDoctor.css';

/**
 * Sanitize user input to prevent XSS
 * Why this matters: User-provided text is sent to backend and may be reflected back
 */
const sanitizeInput = (input: string): string => {
  return DOMPurify.sanitize(input.trim(), { ALLOWED_TAGS: [] });
};

// LocalStorage key for persisting diagnosis session
const STORAGE_KEY = 'citrus_doctor_session';

// ===== TYPE DEFINITIONS =====

export interface DiagnosisIssue {
  id: string;
  name: string;
  type: 'deficiency' | 'pest' | 'disease' | 'environmental' | 'care';
  severity: 'low' | 'medium' | 'high';
  confidence: number;
  description: string;
  likelyCauses: string[];
  affectedAreas: string[];
}

export interface ProductRecommendation {
  name: string;
  productUrl: string;
  whyItHelps: string;
}

export interface DiagnosisRecommendation {
  priority: number;
  action: string;
  reasoning: string;
  threePillarsCategory?: 'soil' | 'microbes' | 'nutrition';
  productRecommendation?: ProductRecommendation;
}

export interface DiagnosisResult {
  overallHealth: 'healthy' | 'minor_issues' | 'moderate_issues' | 'severe_issues';
  healthScore: number;
  issues: DiagnosisIssue[];
  recommendations: DiagnosisRecommendation[];
  generalCareAdvice: string;
  seasonalConsiderations?: string;
  imagesAnalyzed: number;
  analysisTimestamp: string;
  modelUsed: string;
}

type DoctorStep = 'upload' | 'analyzing' | 'preview' | 'email' | 'results';

interface CitrusDoctorProps {
  apiEndpoint?: string;
  onComplete?: (data: { diagnosis: DiagnosisResult; email?: string }) => void;
}

// ===== ANALYZING STEP COMPONENT =====

/**
 * AnalyzingStep - Shows animated progress through analysis stages
 * Why this matters: Provides visual feedback as AI processes images,
 * moving through each step with checkmarks to show progress
 */
const AnalyzingStep: React.FC<{ images: UploadedImage[] }> = ({ images }) => {
  const [currentStep, setCurrentStep] = React.useState(0);
  
  // Animate through steps with realistic timing gaps
  React.useEffect(() => {
    const timer1 = setTimeout(() => setCurrentStep(1), 4500);  // Processing → Identifying (4.5s)
    const timer2 = setTimeout(() => setCurrentStep(2), 8500);  // Identifying → Generating (8s gap)
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const steps = [
    'Processing images',
    'Identifying symptoms', 
    'Generating recommendations'
  ];

  return (
    <div className="doctor-step doctor-step-analyzing">
      {/* Image Scanning Grid */}
      <div className="analyzing-images-grid">
        {images.map((img, index) => (
          <div 
            key={img.id} 
            className="analyzing-image-card"
            style={{ animationDelay: `${index * 0.2}s` }}
          >
            <div className="analyzing-image-wrapper">
              <img 
                src={img.previewUrl} 
                alt={img.label || `Tree image ${index + 1}`} 
              />
              <div className="scan-overlay">
                <div className="scan-line" />
              </div>
              <div className="scan-corners">
                <span className="corner tl" />
                <span className="corner tr" />
                <span className="corner bl" />
                <span className="corner br" />
              </div>
            </div>
            <span className="analyzing-image-label">
              {img.label || `Image ${index + 1}`}
            </span>
          </div>
        ))}
      </div>
      
      {/* Analysis Status */}
      <div className="doctor-analyzing-content">
        <h2 className="doctor-analyzing-title">
          <span className="analyzing-icon">🔬</span>
          Analyzing Your Tree
        </h2>
        <p className="doctor-analyzing-text">
          AI is scanning {images.length} image{images.length > 1 ? 's' : ''} for health indicators...
        </p>
        <div className="doctor-analyzing-steps">
          {steps.map((stepText, index) => {
            const isCompleted = index < currentStep;
            const isActive = index === currentStep;
            
            return (
              <div 
                key={stepText}
                className={`analyzing-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}
              >
                <span className="step-indicator">
                  {isCompleted ? '✓' : <span className="step-dot" />}
                </span>
                <span>{stepText}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ===== MAIN COMPONENT =====

const CitrusDoctor: React.FC<CitrusDoctorProps> = ({
  apiEndpoint = '/api/citrus-doctor',
  onComplete
}) => {
  // State
  const [step, setStep] = useState<DoctorStep>('upload');
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [additionalContext, setAdditionalContext] = useState('');
  const [diagnosisId, setDiagnosisId] = useState<string | null>(null);
  const [diagnosis, setDiagnosis] = useState<DiagnosisResult | null>(null);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Mobile scroll fix - Add body class to enable scrolling on CitrusDoctor page
   * Why this matters: Ensures mobile browsers can scroll the diagnosis page
   * by adding a class that enables touch scrolling.
   */
  useEffect(() => {
    document.body.classList.add('citrus-doctor-active');
    document.documentElement.classList.add('citrus-doctor-active');
    
    return () => {
      document.body.classList.remove('citrus-doctor-active');
      document.documentElement.classList.remove('citrus-doctor-active');
    };
  }, []);

  /**
   * Load saved session from localStorage on mount
   * Why this matters: Restores diagnosis results if user refreshes the page
   */
  useEffect(() => {
    try {
      const savedSession = localStorage.getItem(STORAGE_KEY);
      if (savedSession) {
        const parsed = JSON.parse(savedSession);
        // Only restore if we have valid diagnosis data
        if (parsed.diagnosis) {
          setDiagnosis(parsed.diagnosis);
          setDiagnosisId(parsed.diagnosisId || null);
          setEmail(parsed.email || '');
          setImages(parsed.images || []);
          setAdditionalContext(parsed.additionalContext || '');
          // Restore to the appropriate step
          setStep(parsed.step || 'preview');
        }
      }
    } catch (err) {
      console.error('Failed to load saved session:', err);
      // Handle specific localStorage errors
      if (err instanceof SyntaxError) {
        // Corrupted JSON - clear it
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      }
    }
  }, []);

  /**
   * Save session to localStorage when diagnosis or step changes
   * Why this matters: Persists state so refresh doesn't lose progress
   */
  useEffect(() => {
    // Only save if we have diagnosis data and are past the upload step
    if (diagnosis && (step === 'preview' || step === 'email' || step === 'results')) {
      try {
        const sessionData = {
          step,
          diagnosis,
          diagnosisId,
          email,
          // Don't persist images in localStorage to avoid quota issues with large base64 data
          images: images.map(img => ({ 
            id: img.id, 
            label: img.label, 
            mimeType: img.mimeType,
            previewUrl: img.previewUrl 
          })),
          additionalContext,
          savedAt: new Date().toISOString()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
      } catch (err) {
        console.error('Failed to save session:', err);
        // Handle quota exceeded error gracefully
        if (err instanceof DOMException && err.name === 'QuotaExceededError') {
          console.warn('localStorage quota exceeded - session will not persist');
          // Optionally notify user
        }
      }
    }
  }, [step, diagnosis, diagnosisId, email, images, additionalContext]);

  /**
   * Handle image changes from upload component
   */
  const handleImagesChange = useCallback((newImages: UploadedImage[]) => {
    setImages(newImages);
    setError(null);
  }, []);

  /**
   * Submit images for diagnosis
   * Why this matters: Sends images to backend for Gemini analysis
   */
  const handleAnalyze = useCallback(async () => {
    if (images.length === 0) {
      setError('Please upload at least one image of your citrus tree.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setStep('analyzing');

    try {
      /**
       * Sanitize user inputs before sending to API
       * Why this matters: Prevents XSS if backend reflects data back without sanitization
       */
      const response = await fetch(`${apiEndpoint}/diagnose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          images: images.map(img => ({
            base64Data: img.base64Data,
            mimeType: img.mimeType,
            label: sanitizeInput(img.label)
          })),
          additionalContext: additionalContext ? sanitizeInput(additionalContext) : undefined
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze images');
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Analysis failed');
      }

      setDiagnosisId(data.diagnosisId);
      setDiagnosis(data.diagnosis);
      setStep('preview');

    } catch (err: any) {
      console.error('Diagnosis error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setStep('upload');
    } finally {
      setIsLoading(false);
    }
  }, [images, additionalContext, apiEndpoint]);

  /**
   * Proceed from preview to email gate
   */
  const handleContinueToEmail = useCallback(() => {
    setStep('email');
  }, []);

  /**
   * Submit email and show full results
   * Why this matters: Captures lead for Klaviyo and unlocks full diagnosis
   */
  const handleEmailSubmit = useCallback(async (submittedEmail: string) => {
    if (!diagnosis || !diagnosisId) {
      setError('Diagnosis data is missing. Please try again.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiEndpoint}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: submittedEmail,
          diagnosisId,
          diagnosis
        }),
      });

      const data = await response.json();

      // Even if Klaviyo fails, show results (don't break UX)
      setEmail(submittedEmail);
      setStep('results');

      // Call onComplete callback
      if (onComplete) {
        onComplete({ diagnosis, email: submittedEmail });
      }

    } catch (err: any) {
      console.error('Email submission error:', err);
      // Still show results even if email submission fails
      setEmail(submittedEmail);
      setStep('results');
      
      if (onComplete) {
        onComplete({ diagnosis, email: submittedEmail });
      }
    } finally {
      setIsLoading(false);
    }
  }, [diagnosis, diagnosisId, apiEndpoint, onComplete]);

  /**
   * Start over with new diagnosis
   * Why this matters: Clears all state AND localStorage so user gets fresh start
   */
  const handleStartOver = useCallback(() => {
    // Clear localStorage first to prevent auto-restore
    localStorage.removeItem(STORAGE_KEY);
    
    setStep('upload');
    setImages([]);
    setAdditionalContext('');
    setDiagnosisId(null);
    setDiagnosis(null);
    setEmail('');
    setError(null);
  }, []);

  /**
   * Go back from preview to upload
   * Why this matters: Clears saved session since user wants to start fresh
   */
  const handleBackToUpload = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setStep('upload');
    setDiagnosis(null);
    setDiagnosisId(null);
  }, []);

  /**
   * Go back from email to preview
   */
  const handleBackToPreview = useCallback(() => {
    setStep('preview');
  }, []);

  return (
    <div className="citrus-doctor">
      <div className="doctor-container">
        {/* Header - Full logo with title section below */}
        <div className="doctor-header">
          <div className="doctor-logo">
            <img 
              src="/uscn-logo.png" 
              alt="US Citrus Nursery"
              className="doctor-logo-img"
            />
          </div>
        </div>
        
        {/* Title Section - Separate from logo */}
        <div className="doctor-title-section">
          <h1 className="doctor-title">Citrus Tree AI Doctor</h1>
          <p className="doctor-subtitle">
            Get expert diagnosis & personalized care recommendations in seconds
          </p>
        </div>

        {/* Content */}
        <div className="doctor-content">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="doctor-step doctor-step-upload">
              <ImageUpload
                images={images}
                onImagesChange={handleImagesChange}
                maxImages={1}
                disabled={isLoading}
              />

              {/* Additional Context Input */}
              <div className="doctor-context-section">
                <label htmlFor="additional-context" className="doctor-context-label">
                  Describe any symptoms or concerns (optional)
                </label>
                <textarea
                  id="additional-context"
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="E.g., Yellow leaves on the lower branches, spots appearing over the last week, tree seems droopy. Include details like how long you've noticed the issue, watering schedule, recent weather changes, or fertilizer usage..."
                  className="doctor-context-input"
                  disabled={isLoading}
                />
              </div>

              {/* Error Display */}
              {error && (
                <div className="doctor-error">
                  {error}
                </div>
              )}

              {/* Analyze Button */}
              <button
                className="doctor-btn doctor-btn-primary"
                onClick={handleAnalyze}
                disabled={images.length === 0 || isLoading}
              >
                {isLoading ? 'Analyzing...' : 'Analyze My Tree'}
              </button>
            </div>
          )}

          {/* Step 2: Analyzing */}
          {step === 'analyzing' && (
            <AnalyzingStep images={images} />
          )}

          {/* Step 3: Preview (also shown blurred behind email gate) */}
          {(step === 'preview' || step === 'email') && diagnosis && (
            <div className={step === 'email' ? 'preview-blurred' : ''}>
              <DiagnosisPreview
                diagnosis={diagnosis}
                onContinue={handleContinueToEmail}
                onBack={handleBackToUpload}
                images={images}
              />
            </div>
          )}

          {/* Step 5: Full Results */}
          {step === 'results' && diagnosis && (
            <FullDiagnosisReport
              diagnosis={diagnosis}
              images={images}
              email={email}
              onStartOver={handleStartOver}
            />
          )}
        </div>

        {/* Footer */}
        <div className="doctor-footer">
          <span>Powered by US Citrus Nursery & Dr. Mani's Magic</span>
        </div>
      </div>

      {/* Step 4: Email Gate - Overlay Modal (outside container for proper overlay) */}
      {step === 'email' && diagnosis && (
        <div className="email-gate-overlay">
          <EmailGate
            diagnosis={diagnosis}
            onSubmit={handleEmailSubmit}
            onBack={handleBackToPreview}
            isLoading={isLoading}
            error={error}
          />
        </div>
      )}
    </div>
  );
};

export default CitrusDoctor;
